/**
 * Admin "SEO" panel — single pane of glass for the Ekselens website's
 * search-engine health.
 *
 *   · Top: 4 metric cards (sitemap URLs, prerendered pages, structured-data
 *     types live, content gaps).
 *   · Middle: pass/warn/fail check list pulled from `/admin/seo/health`.
 *   · Bottom: sitemap breakdown + structured-data summary + one-click links
 *     to Google Search Console, Rich Results Test, Mobile-Friendly Test,
 *     Bing Webmaster Tools and the Schema.org validator (pre-filled with
 *     the current production URL).
 *
 * No write actions — this panel is read-only.
 */
import React, { useCallback, useEffect, useState } from "react";
import {
  RefreshCw,
  CheckCircle2,
  AlertTriangle,
  Info,
  ExternalLink,
  Search,
  Smartphone,
  Code2,
  Globe,
  FileText,
  Mail,
  Send,
  Clock,
} from "lucide-react";
import { toast } from "sonner";
import { API_BASE } from "../../lib/api";
import apiClient from "../../lib/api";

const api = apiClient;

const StatusBadge = ({ status }) => {
  if (status === "pass") {
    return (
      <span className="inline-flex items-center gap-1 text-emerald-700 text-[11px] font-semibold tracking-[0.14em] uppercase">
        <CheckCircle2 size={12} /> Pass
      </span>
    );
  }
  if (status === "warn") {
    return (
      <span className="inline-flex items-center gap-1 text-amber-700 text-[11px] font-semibold tracking-[0.14em] uppercase">
        <AlertTriangle size={12} /> Warn
      </span>
    );
  }
  if (status === "fail") {
    return (
      <span className="inline-flex items-center gap-1 text-red-700 text-[11px] font-semibold tracking-[0.14em] uppercase">
        <AlertTriangle size={12} /> Fail
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 ek-text-muted text-[11px] font-semibold tracking-[0.14em] uppercase">
      <Info size={12} /> Info
    </span>
  );
};

const MetricCard = ({ label, value, sub, accent = false, testid }) => (
  <div
    className={`border ek-surface p-5 ${accent ? "border-[#7737F6]" : "ek-line"}`}
    data-testid={testid}
  >
    <div className="text-[10.5px] font-medium tracking-[0.18em] ek-text-muted uppercase mb-2">
      {label}
    </div>
    <div className="text-3xl font-medium text-[#7737F6] tabular-nums">{value}</div>
    {sub ? <div className="text-[11px] ek-text-muted mt-1">{sub}</div> : null}
  </div>
);

const ToolButton = ({ href, icon: Icon, label, sub, testid }) => (
  <a
    href={href}
    target="_blank"
    rel="noreferrer noopener"
    className="block border ek-line ek-surface hover:border-[#7737F6] transition-colors p-4 group"
    data-testid={testid}
  >
    <div className="flex items-center gap-3 mb-1">
      <Icon size={16} className="text-[#7737F6]" />
      <span className="text-[13px] font-semibold ek-text">{label}</span>
      <ExternalLink size={11} className="ek-text-muted ml-auto group-hover:text-[#7737F6]" />
    </div>
    {sub ? <div className="text-[11.5px] ek-text-muted leading-snug">{sub}</div> : null}
  </a>
);

export default function SeoPanel() {
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(true);
  const [digestHistory, setDigestHistory] = useState([]);
  const [digestLoading, setDigestLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [clearingCache, setClearingCache] = useState(false);
  const [pinging, setPinging] = useState(false);
  const [previewBust, setPreviewBust] = useState(Date.now());

  const reload = useCallback(async () => {
    setLoading(true);
    setDigestLoading(true);
    try {
      const [{ data: h }, { data: hist }] = await Promise.all([
        api.get("/admin/seo/health"),
        api.get("/admin/seo/digest/history"),
      ]);
      setHealth(h);
      setDigestHistory(hist?.items || []);
    } catch {
      toast.error("Failed to load SEO health");
    } finally {
      setLoading(false);
      setDigestLoading(false);
    }
  }, []);

  const sendDigestNow = useCallback(async () => {
    setSending(true);
    try {
      const { data } = await api.post("/admin/seo/digest/run");
      if (data?.sent) {
        toast.success(`Digest emailed to ${data.recipient} (${data.file_size_kb} KB)`);
      } else {
        toast.error(`Digest generated but not sent: ${data?.reason || "no email id"}`);
      }
      // Refresh history
      const { data: hist } = await api.get("/admin/seo/digest/history");
      setDigestHistory(hist?.items || []);
    } catch (e) {
      toast.error(`Digest failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setSending(false);
    }
  }, []);

  const clearOgCache = useCallback(async () => {
    setClearingCache(true);
    try {
      const { data } = await api.post("/admin/seo/og-cache/clear");
      toast.success(`Cleared ${data.evicted} cached OG image(s)`);
      setPreviewBust(Date.now()); // force re-fetch of preview <img> tags
    } catch (e) {
      toast.error(`Cache clear failed: ${e.message}`);
    } finally {
      setClearingCache(false);
    }
  }, []);

  const pingSearchEngines = useCallback(async () => {
    setPinging(true);
    try {
      const { data } = await api.post("/admin/seo/indexnow/submit");
      if (data?.submitted) {
        toast.success(`Pinged ${data.total_urls} URL(s) to IndexNow (Bing / Yandex).`);
      } else {
        toast.warning(
          `Submitted ${data?.total_urls || 0} URL(s) — IndexNow returned ${data?.status_code || data?.reason || "no confirmation"}. ` +
            "This is expected until the key file is live on the production domain (redeploy required)."
        );
      }
    } catch (e) {
      toast.error(`IndexNow ping failed: ${e?.response?.data?.detail || e.message}`);
    } finally {
      setPinging(false);
    }
  }, []);

  useEffect(() => {
    reload();
  }, [reload]);

  const siteUrl = health?.site_url || "https://www.ekselens.com";
  const sitemapUrl = health?.sitemap?.url || `${siteUrl}/api/sitemap.xml`;
  const sampleServiceUrl = `${siteUrl}/services/ai-readiness-and-adoption`;

  const overallStatus = (() => {
    const checks = health?.checks || [];
    if (!checks.length) return "info";
    if (checks.some((c) => c.status === "fail")) return "fail";
    if (checks.some((c) => c.status === "warn")) return "warn";
    if (checks.every((c) => c.status === "pass")) return "pass";
    return "info";
  })();

  const overallLabel =
    overallStatus === "pass"
      ? "All checks passing"
      : overallStatus === "warn"
      ? "Action recommended"
      : overallStatus === "fail"
      ? "Action required"
      : "Awaiting checks";

  return (
    <div data-testid="admin-seo-panel">
      <div className="flex items-center justify-between gap-4 mb-6">
        <div>
          <h2 className="ek-heading text-xl">SEO health</h2>
          <p className="ek-text-muted text-[13px] mt-1 max-w-2xl">
            Live view of how this site is set up for search engines and AI assistants — sitemap reach, structured-data coverage, content gaps and prerender status. Read-only.
          </p>
        </div>
        <button
          type="button"
          onClick={reload}
          disabled={loading}
          className="ek-text-muted hover:text-black text-[12px] flex items-center gap-1 disabled:opacity-50"
          data-testid="seo-refresh"
        >
          <RefreshCw size={12} /> Refresh
        </button>
      </div>

      {/* Top-line metric cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <MetricCard
          label="Sitemap URLs"
          value={loading ? "…" : health?.sitemap?.total_urls ?? "—"}
          sub="Self-updating from live content"
          accent
          testid="seo-metric-sitemap"
        />
        <MetricCard
          label="Prerendered pages"
          value={loading ? "…" : health?.prerender?.files_count ?? "—"}
          sub={
            health?.prerender?.build_dir_present
              ? "Ready to ship to production"
              : "Generated by yarn build"
          }
          testid="seo-metric-prerender"
        />
        <MetricCard
          label="Structured-data types"
          value={loading ? "…" : Object.keys(health?.structured_data || {}).length}
          sub="Org, Service, Article, FAQ, Breadcrumbs, Site"
          testid="seo-metric-jsonld"
        />
        <MetricCard
          label="Content gaps"
          value={
            loading
              ? "…"
              : (health?.insights?.without_description || 0) +
                (health?.service_faqs?.without_rich_faqs || 0)
          }
          sub="Insights without summaries + services without FAQs"
          testid="seo-metric-gaps"
        />
      </div>

      {/* Overall status banner */}
      <div
        className={`p-4 mb-8 border ${
          overallStatus === "pass"
            ? "bg-emerald-50 border-emerald-200"
            : overallStatus === "warn"
            ? "bg-amber-50 border-amber-200"
            : overallStatus === "fail"
            ? "bg-red-50 border-red-200"
            : "ek-surface-2 ek-line"
        }`}
        data-testid="seo-overall-banner"
      >
        <div className="flex items-center gap-2">
          <StatusBadge status={overallStatus} />
          <span className="text-[14px] font-medium ek-text">{overallLabel}</span>
        </div>
        <p className="text-[12.5px] ek-text-soft mt-1">
          Last refreshed just now. Re-run after publishing new insights or service updates.
        </p>
      </div>

      {/* Check list */}
      <div className="mb-10">
        <h3 className="text-[12px] font-semibold tracking-[0.18em] uppercase ek-text-muted mb-3">
          Health checks
        </h3>
        <div className="border ek-line ek-surface divide-y divide-zinc-100">
          {loading ? (
            <div className="px-4 py-6 ek-text-muted text-[13px]">Loading…</div>
          ) : !health?.checks?.length ? (
            <div className="px-4 py-6 ek-text-muted text-[13px]">No checks to display.</div>
          ) : (
            health.checks.map((c, i) => (
              <div
                key={c.name}
                className="flex items-start justify-between gap-4 px-4 py-3"
                data-testid={`seo-check-${i}`}
              >
                <div className="flex-1">
                  <div className="text-[13px] font-medium ek-text">{c.name}</div>
                  <div className="text-[12px] ek-text-soft mt-0.5">{c.detail}</div>
                </div>
                <StatusBadge status={c.status} />
              </div>
            ))
          )}
        </div>
      </div>

      {/* Sitemap breakdown + Structured data */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-10">
        <div>
          <h3 className="text-[12px] font-semibold tracking-[0.18em] uppercase ek-text-muted mb-3">
            Sitemap breakdown
          </h3>
          <div className="border ek-line ek-surface">
            <table className="w-full text-[13px]">
              <tbody>
                {Object.entries(health?.sitemap?.breakdown || {}).map(([k, v]) => (
                  <tr key={k} className="border-t ek-line first:border-t-0">
                    <td className="px-4 py-2.5 ek-text-soft capitalize">{k.replace(/_/g, " ")}</td>
                    <td className="px-4 py-2.5 text-right font-medium text-[#7737F6] tabular-nums">{v}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <a
            href={sitemapUrl}
            target="_blank"
            rel="noreferrer noopener"
            className="inline-flex items-center gap-1 text-[12px] text-[#7737F6] hover:underline mt-2"
            data-testid="seo-sitemap-link"
          >
            <FileText size={12} /> View live sitemap.xml <ExternalLink size={10} />
          </a>
          <div className="mt-4 border-t ek-line pt-4">
            <button
              type="button"
              onClick={pingSearchEngines}
              disabled={pinging}
              className="inline-flex items-center gap-2 text-[12px] font-medium bg-[#7737F6] text-white px-3.5 py-2 hover:bg-[#5f27d6] disabled:opacity-60 transition-colors"
              data-testid="seo-indexnow-ping"
            >
              <Send size={13} className={pinging ? "animate-pulse" : ""} />
              {pinging ? "Pinging search engines…" : "Ping search engines now (IndexNow)"}
            </button>
            <p className="text-[11.5px] ek-text-muted mt-2 max-w-md leading-relaxed">
              Instantly notifies Bing, Yandex and Seznam of every indexable URL so new or updated
              content is picked up in minutes. New case studies and insights ping automatically; use
              this for an on-demand full submission.
            </p>
          </div>

          <div className="mt-4 border-t ek-line pt-4" data-testid="seo-postdeploy-checklist">
            <h4 className="text-[12px] font-semibold tracking-[0.16em] uppercase ek-text-muted mb-2">
              Post-deploy SEO checklist
            </h4>
            <p className="text-[11.5px] ek-text-muted mb-3 max-w-md leading-relaxed">
              Prerendered HTML, the sitemap and the IndexNow key file only go live on redeploy.
              Run these once after each production deploy so new/changed pages are re-crawled quickly.
            </p>
            <ul className="space-y-2">
              {[
                "Confirm the deploy succeeded and the site loads on the production domain.",
                "Click \u201CPing search engines now\u201D above (submits every indexable URL to IndexNow).",
                "Resubmit the sitemap in Google Search Console (Sitemaps \u2192 /api/sitemap.xml).",
                "Resubmit the sitemap in Bing Webmaster Tools.",
                "Spot-check a key page\u2019s robots meta is \u201Cindex, follow\u201D (View source \u2192 <meta name=\u201Crobots\u201D>).",
              ].map((step, i) => (
                <li key={i} className="flex items-start gap-2 text-[12px] ek-text-soft leading-relaxed"
                    data-testid={`seo-checklist-step-${i}`}>
                  <CheckCircle2 size={14} className="text-[#7737F6] mt-0.5 shrink-0" />
                  <span>{step}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div>
          <h3 className="text-[12px] font-semibold tracking-[0.18em] uppercase ek-text-muted mb-3">
            Structured data emitted
          </h3>
          <div className="border ek-line ek-surface">
            <table className="w-full text-[13px]">
              <tbody>
                {Object.entries(health?.structured_data || {}).map(([k, v]) => (
                  <tr key={k} className="border-t ek-line first:border-t-0">
                    <td className="px-4 py-2.5 ek-text-soft capitalize">{k}</td>
                    <td className="px-4 py-2.5 text-right ek-text-muted text-[12px]">{v}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* External tools */}
      <div>
        <h3 className="text-[12px] font-semibold tracking-[0.18em] uppercase ek-text-muted mb-3">
          Test on Google / Bing
        </h3>
        <p className="text-[12px] ek-text-muted mb-3">
          One-click links to validate live URLs. Sample: <code className="text-[11px] ek-text-soft">{sampleServiceUrl}</code>
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          <ToolButton
            href={`https://search.google.com/test/rich-results?url=${encodeURIComponent(sampleServiceUrl)}`}
            icon={Search}
            label="Google Rich Results Test"
            sub="Validates JSON-LD (FAQ, Breadcrumbs, Article, Service)"
            testid="seo-tool-rich-results"
          />
          <ToolButton
            href={`https://search.google.com/test/mobile-friendly?url=${encodeURIComponent(siteUrl)}`}
            icon={Smartphone}
            label="Google Mobile-Friendly Test"
            sub="Confirms responsive design / viewport"
            testid="seo-tool-mobile"
          />
          <ToolButton
            href="https://search.google.com/search-console"
            icon={Globe}
            label="Google Search Console"
            sub="Submit sitemap, monitor indexing, see search queries"
            testid="seo-tool-gsc"
          />
          <ToolButton
            href="https://www.bing.com/webmasters"
            icon={Globe}
            label="Bing Webmaster Tools"
            sub="Submit sitemap, monitor Bing/DuckDuckGo indexing"
            testid="seo-tool-bing"
          />
          <ToolButton
            href={`https://validator.schema.org/#url=${encodeURIComponent(sampleServiceUrl)}`}
            icon={Code2}
            label="Schema.org Validator"
            sub="Validates all structured data on a URL"
            testid="seo-tool-schema"
          />
          <ToolButton
            href={`https://www.linkedin.com/post-inspector/inspect/${encodeURIComponent(siteUrl)}`}
            icon={Search}
            label="LinkedIn Post Inspector"
            sub="Preview how the homepage appears when shared on LinkedIn"
            testid="seo-tool-linkedin"
          />
        </div>
      </div>

      {/* Social share previews */}
      <div className="mt-10">
        <div className="flex items-end justify-between mb-3">
          <div>
            <h3 className="text-[12px] font-semibold tracking-[0.18em] uppercase ek-text-muted">
              Social share previews
            </h3>
            <p className="text-[12px] ek-text-muted mt-1">
              Branded 1200×630 cards generated per route. This is what visitors see when one of these URLs is shared on LinkedIn, Slack, WhatsApp, Twitter, Facebook or iMessage. Cached server-side for 24 hours.
            </p>
          </div>
          <button
            type="button"
            onClick={clearOgCache}
            disabled={clearingCache}
            className="ek-text-soft hover:text-black text-[12px] flex items-center gap-1 border ek-line px-3 py-1.5 disabled:opacity-50"
            data-testid="seo-og-cache-clear"
          >
            <RefreshCw size={12} /> {clearingCache ? "Clearing…" : "Clear OG cache"}
          </button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[
            { label: "Home", route: "/", path: "page/home" },
            { label: "Services hub", route: "/services", path: "page/services" },
            { label: "Service — AI Readiness", route: "/services/ai-readiness-and-adoption", path: "service/ai-readiness-and-adoption" },
            { label: "Lite — PMO360", route: "/resources/pmo360-self-assessment", path: "lite/pmo360" },
            { label: "Resources hub", route: "/resources", path: "page/resources" },
            { label: "Contact", route: "/contact", path: "page/contact" },
          ].map((it) => {
            const src = `${API_BASE}/og/${it.path}.png?v=${previewBust}`;
            return (
              <a
                key={it.path}
                href={src}
                target="_blank"
                rel="noreferrer noopener"
                className="block border ek-line ek-surface hover:border-[#7737F6] transition-colors overflow-hidden"
                data-testid={`seo-og-preview-${it.path.replace(/\//g, "-")}`}
              >
                <div className="aspect-[1200/630] ek-surface-2">
                  <img
                    src={src}
                    alt={`OG preview for ${it.label}`}
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                </div>
                <div className="px-3 py-2 flex items-center justify-between gap-2">
                  <div>
                    <div className="text-[12px] font-semibold ek-text truncate">{it.label}</div>
                    <code className="text-[11px] ek-text-muted">{it.route}</code>
                  </div>
                  <ExternalLink size={11} className="ek-text-muted flex-shrink-0" />
                </div>
              </a>
            );
          })}
        </div>
      </div>

      {/* Weekly SEO digest */}
      <div className="mt-10">
        <div className="flex items-end justify-between mb-3">
          <div>
            <h3 className="text-[12px] font-semibold tracking-[0.18em] uppercase ek-text-muted">
              Weekly SEO digest
            </h3>
            <p className="text-[12px] ek-text-muted mt-1">
              Auto-emailed every <strong>Monday at 06:30 UTC</strong>. One-page branded PDF with sitemap delta, content gaps and a quick action checklist.
            </p>
          </div>
          <button
            type="button"
            onClick={sendDigestNow}
            disabled={sending}
            className="ek-btn ek-btn-primary text-[12.5px] disabled:opacity-50 flex items-center gap-1"
            data-testid="seo-digest-send"
          >
            <Send size={13} />
            {sending ? "Sending…" : "Send digest now"}
          </button>
        </div>

        <div className="border ek-line ek-surface">
          {digestLoading ? (
            <div className="px-4 py-6 ek-text-muted text-[13px]">Loading history…</div>
          ) : digestHistory.length === 0 ? (
            <div className="px-4 py-6 ek-text-muted text-[13px] flex items-center gap-2">
              <Info size={14} />
              No digests sent yet. Click "Send digest now" to fire the first one — future weeks will show delta vs this baseline.
            </div>
          ) : (
            <table className="w-full text-[13px]">
              <thead>
                <tr className="border-b ek-line">
                  <th className="text-left px-4 py-2.5 text-[11px] tracking-[0.14em] uppercase ek-text-muted font-medium">Sent</th>
                  <th className="text-left px-4 py-2.5 text-[11px] tracking-[0.14em] uppercase ek-text-muted font-medium">Recipient</th>
                  <th className="text-right px-4 py-2.5 text-[11px] tracking-[0.14em] uppercase ek-text-muted font-medium">URLs</th>
                  <th className="text-right px-4 py-2.5 text-[11px] tracking-[0.14em] uppercase ek-text-muted font-medium">Δ</th>
                  <th className="text-right px-4 py-2.5 text-[11px] tracking-[0.14em] uppercase ek-text-muted font-medium">Checks</th>
                  <th className="text-right px-4 py-2.5 text-[11px] tracking-[0.14em] uppercase ek-text-muted font-medium">Gaps</th>
                  <th className="text-right px-4 py-2.5 text-[11px] tracking-[0.14em] uppercase ek-text-muted font-medium">Status</th>
                </tr>
              </thead>
              <tbody>
                {digestHistory.map((r, i) => {
                  const ts = new Date(r.generated_at);
                  return (
                    <tr
                      key={r.id || i}
                      className="border-b ek-line last:border-b-0"
                      data-testid={`seo-digest-row-${i}`}
                    >
                      <td className="px-4 py-2.5">
                        <div className="flex items-center gap-1.5 ek-text-soft">
                          <Clock size={11} className="ek-text-muted" />
                          {ts.toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}
                        </div>
                      </td>
                      <td className="px-4 py-2.5 ek-text-soft">
                        <div className="flex items-center gap-1.5">
                          <Mail size={11} className="ek-text-muted" />
                          {r.recipient || "—"}
                        </div>
                      </td>
                      <td className="px-4 py-2.5 text-right tabular-nums font-medium text-[#7737F6]">{r.sitemap_total}</td>
                      <td className={`px-4 py-2.5 text-right tabular-nums ${r.total_delta > 0 ? "text-emerald-700" : r.total_delta < 0 ? "text-red-700" : "ek-text-muted"}`}>
                        {r.first_run ? "—" : r.total_delta > 0 ? `+${r.total_delta}` : r.total_delta}
                      </td>
                      <td className="px-4 py-2.5 text-right tabular-nums ek-text-soft">{r.checks_passing}/{r.checks_total}</td>
                      <td className={`px-4 py-2.5 text-right tabular-nums ${r.content_gaps > 0 ? "text-amber-700" : "ek-text-muted"}`}>{r.content_gaps}</td>
                      <td className="px-4 py-2.5 text-right">
                        {r.sent ? (
                          <span className="inline-flex items-center gap-1 text-emerald-700 text-[11px] font-semibold tracking-[0.14em] uppercase">
                            <CheckCircle2 size={11} /> Sent
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-red-700 text-[11px] font-semibold tracking-[0.14em] uppercase">
                            <AlertTriangle size={11} /> Failed
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Notes footer */}
      <div className="mt-10 border border-dashed ek-line ek-surface-2 p-4">
        <h4 className="text-[12px] font-semibold tracking-[0.18em] uppercase ek-text-muted mb-2">
          Notes
        </h4>
        <ul className="text-[12.5px] ek-text-soft space-y-1 list-disc pl-5">
          <li>
            The sitemap is regenerated dynamically on every request — there is no caching to refresh.
          </li>
          <li>
            Prerendered HTML files only ship when <code className="text-[11px]">yarn build</code> runs in production. Preview env shows the build folder if a build has been run locally; otherwise <code className="text-[11px]">files_count</code> will be 0.
          </li>
          <li>
            After DNS cutover, submit the live sitemap URL in Google Search Console + Bing Webmaster Tools to accelerate indexing.
          </li>
        </ul>
      </div>
    </div>
  );
}
